import { Component, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NetworkStatusService, User } from '@caballus/ui-common';
import { Capacitor } from '@capacitor/core';
import { AuthState, UserState } from '@ion-caballus/core/state';
import { Platform } from '@ionic/angular';
import { Select } from '@ngxs/store';
import { BehaviorSubject, Observable, from, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { loadGoogleMaps } from '../google-maps-loader';
import { loadJWPlayer } from '../jw-player-loader';

declare let cordova: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
    @Select(AuthState.authToken)
    public authToken$!: Observable<string>;
    @Select(AuthState.refreshToken)
    public refreshToken$!: Observable<string>;
    @Select(UserState.user)
    public user$!: Observable<User>;
    public isOnline$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public readonly title: string = 'Caballus';

    constructor(
        private readonly _router: Router,
        private readonly _platform: Platform,
        private readonly _networkStatusService: NetworkStatusService,
        private readonly _ngZone: NgZone
    ) {}

    public ngOnInit(): void {
        from(this.loadExternalResources())
            .pipe(
                catchError(error => {
                    return of(null);
                })
            )
            .subscribe();

        if (Capacitor.isNativePlatform()) {
            document.addEventListener(
                'deviceready',
                () => {
                    /**
                     * This event's listener will aid in determining whether or not
                     * the background Initiation of the mode plugin failed
                     */
                    cordova.plugins.backgroundMode.on('failure', () => {});
                },
                false
            );
        }

        this._networkStatusService.continuousNetworkStatus$
            .pipe(
                map(status => status.isOnline),
                distinctUntilChanged(),
                switchMap(isOnline => {
                    if (isOnline) {
                        return of(true);
                    } else {
                        return timer(5000).pipe(map(() => false));
                    }
                })
            )
            .subscribe(isOnline => {
                this.isOnline$.next(isOnline);
                if (isOnline) {
                    this.retryLoadExternalResources();
                }
            });
    }

    private async loadExternalResources(): Promise<void> {
        if (this.isOnline$.value) {
            try {
                await Promise.all([loadGoogleMaps(), loadJWPlayer()]);
            } catch (error) {}
        }
    }

    private retryLoadExternalResources(): void {
        if (this.isOnline$.value) {
            from(this.loadExternalResources())
                .pipe(
                    catchError(error => {
                        return of(null);
                    })
                )
                .subscribe();
        }
    }
}
